// ---- Cooking (client) --------------------------------------------------------
// The sixth focus.ts-sibling activity (plan 18, reworked by plan 25), beside
// combat.ts (fight), talk.ts (converse), interact.ts (props), fishing.ts
// (angle), and hunting.ts (stalk). The active branch — Cooking — is the SINK
// that closes the fish/meat loop: a raw ingredient becomes an edible dish.
//
//   COOKING (active, plan 25 — HEARTH-GATED): cooking happens AT a fire — a
//   kitchen range (the `range` deco; click it, press F, or hit the action-bar
//   Cook slot) or a player-started campfire. Entering COOK MODE frames the pan
//   over the fire (cookfx.ts pan rig + the first-person cook camera) and opens
//   the bag ALONGSIDE; picking a cookable in the bag sets its model on the pan
//   and sends `cook`. The SERVER validates the hearth + rolls a seed → `panOn`
//   → both sides expand the SAME prompt pattern and the PAN MINIGAME mounts
//   (src/minigame/pan.ts — FLIP/STIR/SEASON/TOSS lanes, the fishing reel's
//   twin): we judge key timing locally for feel and stream `panInput`; the
//   server re-derives every hit from arrival time and owns the verdict, which
//   lands as `cookResult` — cooked plates the dish, a full BURN meter mints a
//   Burnt Mess. After a result we STAY in cook mode (cook another / Esc out).
//
//   EATING: the Eat button on a food item → `consume`. The HP heal is
//   server-authoritative (comes back as `vitals`, presented with no flinch since
//   HP rises); the stamina restore is predicted here (stamina is client-owned in
//   the HUD and isn't a dupe vector — the server bumps its own gate pool too).
//
//   REMOTE PANS: while anyone cooks, their pan + sizzling ingredient render for
//   every nearby player — NetPlayer.cooking/hx/hz ride the snapshot (the
//   fishing cx/cz bobber-mirror pattern) into setRemotePan/clearRemotePan.
//
// Wiring rule (game.ts): initCooking runs AFTER initHunting and BEFORE initFocus
// — its canvas pointerdown must win the click while cook mode is live, and its
// Esc must exit before focus.ts would drop focus.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { clearFocus, getFireFocus } from "./focus.ts";
import { itemSpec, buildItemMesh } from "./items/index.ts";
import { isCookable, isFood } from "./items/define.ts";
import { countOf, flashToast, openInventory, closeInventory, setCookGate } from "./inventory.ts";
import { sendCook, sendPanInput, sendCookEnd, sendConsume } from "./net.ts";
import { resetCookCamera } from "./camera.ts";
import { hasBoxUnlocked } from "./skill.ts";
import { SKILLS, cookingSum } from "./skill/index.ts";
import { COOKING_TUNING as CT } from "./tunables.ts";
import { genPanPattern } from "./sim/cooking.ts";
import { makePanMinigame } from "./minigame/pan.ts";
import type { Minigame } from "./minigame/types.ts";
import { makePanRig, updateFlames, RANGE_COOKTOP_Y } from "./cookfx.ts";
import { heightAt } from "./terrain.ts";
import { ACTIVE_MAP_ID } from "./map/active.ts";
import { mapById } from "./content/load.ts";
import type { ItemType } from "./shared.ts";

const COOK_TIMEOUT = 30;   // safety: max seconds awaiting a server reply mid-session
const COOK_EXIT_DUR = 0.5; // camera ease-back window (mirrors talkExit/fishingExit)

/** A hearth the player can cook at: a kitchen range deco or (CP8) a campfire. */
export type Hearth = { x: number; z: number; kind: "range" | "fire" };

type Mode = "idle" | "select" | "pan";
let mode: Mode = "idle";
let pending = false; // `cook` sent, waiting on panOn/cookResult — don't double-send
let waitT = 0;       // seconds since we started waiting on the server (safety timeout)
let minigame: Minigame | null = null;

let scene: THREE.Scene;
let promptEl: HTMLElement | null = null;
let promptShown = "";

// ---- The pan rig (cookfx) at the hearth + the dish cache -------------------------
let panRig: THREE.Group | null = null;
let dishAnchor: THREE.Group | null = null;
let curDish: THREE.Group | null = null;
const dishCache = new Map<ItemType, THREE.Group>(); // built once per raw type — no per-cook rebuild/leak
const panPt = { x: 0, y: 0, z: 0 }; // the pan's world point (game.ts aims the camera here)

/** The pan rig's base for a hearth: a range's iron cooktop, or just over the
 *  ground for a campfire. Shared by the local rig and the remote mirrors. */
function panBase(h: Hearth): { x: number; y: number; z: number } {
  return { x: h.x, y: heightAt(h.x, h.z) + (h.kind === "range" ? RANGE_COOKTOP_Y : 0.1), z: h.z };
}

function showPrompt(text: string, urgent = false): void {
  if (!promptEl) return;
  if (text !== promptShown) {
    promptShown = text;
    promptEl.textContent = text;
    promptEl.style.display = text ? "block" : "none";
  }
  promptEl.classList.toggle("urgent", urgent && !!text);
}

/** Some other activity owns the input — don't enter cook mode. */
function busy(): boolean {
  return inputLocked();
}

/** The folded cooking box effects (pan window mult etc.) — THE shared fold
 *  (skill/define.ts), over the mirrored unlocked boxes. Pure UX; the server
 *  re-derives its own. */
function boxFx() {
  return cookingSum(SKILLS, hasBoxUnlocked);
}

// ---- Hearth discovery -------------------------------------------------------------
// The static half: the active map doc's authored `range` decos (cached per map
// id — content is static). The dynamic half (live campfires) is injected by
// campfire.ts via setLiveFires so the import graph stays one-way.

let rangeCache: { mapId: string; ranges: { x: number; z: number }[] } | null = null;

function mapRanges(): { x: number; z: number }[] {
  if (!rangeCache || rangeCache.mapId !== ACTIVE_MAP_ID) {
    const doc = mapById(ACTIVE_MAP_ID);
    rangeCache = {
      mapId: ACTIVE_MAP_ID,
      ranges: (doc?.decos ?? []).filter((d) => d.type === "range").map((d) => ({ x: d.x, z: d.z })),
    };
  }
  return rangeCache.ranges;
}

let liveFires: () => Iterable<{ x: number; z: number }> = () => [];
/** campfire.ts (CP8) registers its live-fire mirror here. */
export function setLiveFires(fn: typeof liveFires): void { liveFires = fn; }

/** The nearest hearth in cooking reach of the player, or null. Client-side UX
 *  gate only — the server re-derives its own from where we stand. */
export function nearHearth(): Hearth | null {
  const p = game.player.position;
  let best: Hearth | null = null, bd = Infinity;
  for (const r of mapRanges()) {
    const d = Math.hypot(p.x - r.x, p.z - r.z);
    if (d <= CT.hearthRange && d < bd) { bd = d; best = { x: r.x, z: r.z, kind: "range" }; }
  }
  for (const f of liveFires()) {
    const d = Math.hypot(p.x - f.x, p.z - f.z);
    if (d <= CT.hearthRange && d < bd) { bd = d; best = { x: f.x, z: f.z, kind: "fire" }; }
  }
  return best;
}

/** Is (x,z) within hearth reach of a kitchen RANGE? (campfires excluded) — the
 *  brewing deploy gate's client half (plan 25: fermenters are kitchen gear). */
export function nearRange(x: number, z: number): boolean {
  for (const r of mapRanges()) {
    if (Math.hypot(x - r.x, z - r.z) <= CT.hearthRange) return true;
  }
  return false;
}

/** Is (x,z) an authored range spot? — classifies a REMOTE cook's broadcast
 *  anchor (their pan sits on the cooktop vs over a campfire). */
function hearthKindAt(x: number, z: number): "range" | "fire" {
  for (const r of mapRanges()) {
    if (Math.hypot(x - r.x, z - r.z) <= 1.2) return "range";
  }
  return "fire";
}

// ---- Cook MODE (enter/exit — pure client presentation) ---------------------------

/** Step up to a hearth: lock input, frame the pan, open the bag alongside.
 *  interact.ts (the range's Cook verb) and the action-bar slot both land here. */
export function enterCookMode(hearth?: Hearth | null): void {
  if (game.cooking || busy()) return;
  const h = hearth ?? nearHearth();
  if (!h) { flashToast("You need a fire — find a kitchen range or start a campfire"); return; }
  clearFocus(); // the first-person cook camera replaces any target lock
  mode = "select";
  pending = false;
  waitT = 0;
  game.cooking = true;
  const base = panBase(h);
  panPt.x = base.x; panPt.y = base.y + 0.3; panPt.z = base.z; // the skillet itself
  if (panRig) {
    panRig.position.set(base.x, base.y, base.z);
    panRig.visible = true;
    if (curDish && dishAnchor) { dishAnchor.remove(curDish); curDish = null; }
  }
  resetCookCamera();
  openInventory(); // the bag rides alongside the pan — pick something to cook
  showPrompt("🍳 Pick an ingredient from your bag — Esc steps away");
}

function exitCookMode(): void {
  if (!game.cooking) return;
  minigame?.unmount();
  minigame = null;
  mode = "idle";
  pending = false;
  game.cooking = false;
  game.cookingExit = COOK_EXIT_DUR; // ease the camera back (game.ts)
  if (panRig) panRig.visible = false;
  if (curDish && dishAnchor) { dishAnchor.remove(curDish); curDish = null; }
  closeInventory();
  showPrompt("");
}

/** Where the pan sits — game.ts aims the first-person cook camera at it. */
export function getCookPoint(): { x: number; y: number; z: number } {
  return panPt;
}

// ---- Starting + resolving a cook ------------------------------------------------

/** Cook a carried raw ingredient (the bag Cook button's action). Enters cook
 *  mode first if we aren't in it (gated on a hearth in reach); the model lands
 *  on the pan optimistically and the server answers `panOn` (or rejects with
 *  `cookResult invalid`). */
export function startCooking(type: ItemType): void {
  if (!game.cooking) { enterCookMode(); if (!game.cooking) return; }
  if (mode !== "select" || pending) return; // a dish is already on the fire
  const spec = itemSpec(type);
  if (!isCookable(spec) || countOf(type) < 1) return;
  // Mount this raw type's dish on the pan, building once + caching (no per-cook
  // geometry churn — the cookable set is small + static).
  if (dishAnchor) {
    if (curDish) dishAnchor.remove(curDish);
    let dish = dishCache.get(type);
    if (!dish) { dish = buildItemMesh(type); dish.scale.setScalar(1.15); dishCache.set(type, dish); }
    dishAnchor.add(dish);
    curDish = dish;
  }
  pending = true;
  waitT = 0;
  showPrompt(`🍳 ${spec.name} on the pan…`);
  sendCook(type);
}

function cancelSession(): void {
  // Esc during a live pan: tell the server (untouched → refund; judged →
  // forfeit, its cookResult lands as a burnt toast) and drop back to select.
  sendCookEnd();
  minigame?.unmount();
  minigame = null;
  mode = "select";
  pending = false;
  if (curDish && dishAnchor) { dishAnchor.remove(curDish); curDish = null; }
  showPrompt("🍳 Pick an ingredient from your bag — Esc steps away");
}

/** Map swap (plan 24, game.ts swapWorld): drop the local cook state without a
 *  server intent (transferCharacter already cleared the cook server-side) —
 *  and every remote pan (their owners get re-entered on the new map). */
export function resetCooking(): void {
  exitCookMode(); // no-op when not cooking
  for (const id of [...remotePans.keys()]) clearRemotePan(id);
  game.cookingExit = 0; // no cross-map exit ease — the swap cuts through a fade
  rangeCache = null;    // the active map changed under us
}

// ---- Server appliers (called by net.ts) ------------------------------------------

/** The server accepted the cook (owner-only): mount the LITERAL pan minigame —
 *  both sides expand the identical pattern from the seed (the fishOn twin). */
export function applyPanOn(seed: number, type: ItemType): void {
  if (!game.cooking) return;
  mode = "pan";
  pending = false;
  waitT = 0;
  const pattern = genPanPattern(seed, CT.cookTier[type] ?? CT.cookTierDefault, CT);
  minigame?.unmount();
  minigame = makePanMinigame({
    pattern,
    hitWindow: CT.panHitWindow * boxFx().panWindowMul,
    tun: CT,
    ev: { onInput: (i, hit) => sendPanInput(i, hit) },
  });
  minigame.mount();
  showPrompt("");
}

/** The cook resolved (owner-only). We STAY in cook mode (cook another / Esc
 *  out); "invalid" = the server rejected/cancelled the session — back out
 *  quietly (wrong spot, rate gate, walked out of hearth reach). */
export function applyCookResult(
  outcome: "cooked" | "burnt" | "invalid",
  item?: ItemType,
): void {
  minigame?.unmount();
  minigame = null;
  if (!game.cooking) return;
  if (outcome === "invalid") { exitCookMode(); return; }
  if (outcome === "cooked" && item) flashToast(`Cooked ${itemSpec(item).name}!`);
  else if (outcome === "burnt") flashToast("Burnt! …a charred mess.");
  mode = "select";
  pending = false;
  waitT = 0;
  if (curDish && dishAnchor) { dishAnchor.remove(curDish); curDish = null; }
  openInventory(); // the cook-another loop — the bag is the menu
  showPrompt("🍳 Pick an ingredient from your bag — Esc steps away");
}

// ---- Eating ---------------------------------------------------------------------

/** Eat one unit of a food dish (the inventory Eat button's action). HP heal is
 *  server-authoritative (vitals); the stamina restore is applied optimistically. */
export function eat(type: ItemType): void {
  if (game.down) return;
  const spec = itemSpec(type);
  if (!isFood(spec) || countOf(type) < 1) return;
  const sta = spec.food.stamina ?? 0;
  if (sta > 0) game.stamina = Math.min(game.maxStamina, game.stamina + sta);
  sendConsume(type);
}

// ---- Remote pans (net.ts drives these from the snapshot) --------------------------
// Everyone sees a pan + sizzling ingredient where another player cooks. Keyed by
// player id (the remoteBobbers pattern). The anchor classifies itself against
// the active map's ranges (cooktop height) vs anything else (campfire, ground).
const remotePans = new Map<string, { g: THREE.Group; type: ItemType | null }>();

/** net.ts: a remote is cooking `type` at hearth (x,z) — show/update their pan. */
export function setRemotePan(id: string, x: number, z: number, type: ItemType): void {
  let r = remotePans.get(id);
  if (!r) {
    r = { g: makePanRig(), type: null };
    scene.add(r.g);
    remotePans.set(id, r);
  }
  const base = panBase({ x, z, kind: hearthKindAt(x, z) });
  r.g.position.set(base.x, base.y, base.z);
  if (r.type !== type) {
    const anchor = r.g.userData.dishAnchor as THREE.Group;
    anchor.clear();
    const dish = buildItemMesh(type);
    dish.scale.setScalar(1.15);
    anchor.add(dish);
    r.type = type;
  }
}

/** net.ts: a remote stopped cooking / left interest / disconnected — drop it. */
export function clearRemotePan(id: string): void {
  const r = remotePans.get(id);
  if (r) scene.remove(r.g);
  remotePans.delete(id);
}

// ---- Action-bar Cook slot (combat.ts renders it) ---------------------------------

/** The cook slot for CAMPFIRES: only while a fire is focused (click the blaze).
 *  Kitchen ranges keep their own click/F `use` interact verb (interact.ts) and
 *  never use this proximity-style slot. While cook mode is live it reads as the
 *  current stage, disabled — the bag and the minigame own the interaction. */
export function cookSlotState(): { show: boolean; icon: string; label: string; enabled: boolean } {
  if (game.cooking) {
    if (mode === "pan" || pending) return { show: true, icon: "🍳", label: "Cooking…", enabled: false };
    return { show: true, icon: "🍳", label: "Pick food", enabled: false };
  }
  if (busy()) return { show: false, icon: "", label: "", enabled: false };
  const ff = getFireFocus();
  if (!ff) return { show: false, icon: "", label: "", enabled: false };
  const p = game.player.position;
  const near = Math.hypot(p.x - ff.x, p.z - ff.z) <= CT.hearthRange + 0.5;
  return {
    show: true,
    icon: "🍳",
    label: near ? "Cook" : "Too far",
    enabled: near,
  };
}

/** The cook slot's click action: step up to the focused campfire. */
export function cookSlotAction(): void {
  if (game.cooking) return;
  const ff = getFireFocus();
  if (!ff) { flashToast("Focus a fire first"); return; }
  const p = game.player.position;
  if (Math.hypot(p.x - ff.x, p.z - ff.z) > CT.hearthRange + 0.5) {
    flashToast("Too far from the fire");
    return;
  }
  enterCookMode({ x: ff.x, z: ff.z, kind: "fire" });
}

// ---- Init + per-frame -----------------------------------------------------------

export function initCooking(s: THREE.Scene, canvas: HTMLCanvasElement): void {
  scene = s;
  promptEl = document.getElementById("cookprompt");
  panRig = makePanRig();
  panRig.visible = false;
  dishAnchor = panRig.userData.dishAnchor as THREE.Group;
  scene.add(panRig);
  document.getElementById("cookBtn")?.addEventListener("click", cookSlotAction);
  // The bag's Cook button greys out away from a fire (inventory.ts renders it;
  // the gate lives here so hearth logic has one home).
  // Bag Cook: in cook mode, OR focused fire in reach, OR near a kitchen range
  // (ranges have no fire-focus path — still use proximity for the bag button).
  setCookGate(() => {
    if (game.cooking) return true;
    const ff = getFireFocus();
    if (ff) {
      const p = game.player.position;
      return Math.hypot(p.x - ff.x, p.z - ff.z) <= CT.hearthRange + 0.5;
    }
    return nearRange(game.player.position.x, game.player.position.z);
  });
  // While cook mode is live the canvas click is ours the WHOLE time (mirrors
  // fishing/hunting) — it never re-targets focus / picks items behind the pan.
  canvas.addEventListener("pointerdown", (e) => {
    if (!game.cooking) return;
    e.stopImmediatePropagation();
  });
  window.addEventListener("keydown", (e) => {
    if (!game.cooking) return;
    if (e.key === "Escape") {
      if (mode === "pan") cancelSession();
      else exitCookMode();
      e.stopImmediatePropagation();
      return;
    }
    // A live pan gets first refusal on WASD/arrows (the lanes beat movement —
    // game.cooking already locks the mover, but focus/others must not see it).
    if (minigame?.key(e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  });
}

/** Per-frame: face the hearth, flicker the flames (local + remote pans), sizzle
 *  the dish, tick the live minigame, and hold a safety timeout so a dropped
 *  server reply can't strand the input lock forever. */
export function updateCooking(dt: number, t: number): void {
  // Remote pans flicker for everyone, cooking or not.
  for (const r of remotePans.values()) {
    updateFlames(r.g.userData.flames as THREE.Object3D, t);
    const anchor = r.g.userData.dishAnchor as THREE.Group;
    anchor.position.y = 0.38 + Math.sin(t * 9) * 0.008;
  }
  if (!game.cooking) { waitT = 0; return; }
  if (game.down) { sendCookEnd(); exitCookMode(); return; } // KO'd mid-cook — the server dropped it too

  // Face the pan while movement is locked.
  const p = game.player.position;
  game.player.rotation.y = Math.atan2(panPt.x - p.x, panPt.z - p.z);

  if (panRig) {
    updateFlames(panRig.userData.flames as THREE.Object3D, t);
    if (dishAnchor && curDish) dishAnchor.position.y = 0.38 + Math.sin(t * 9) * 0.01; // sizzle jitter
  }
  minigame?.tick(dt);

  // Safety timeout: only while a server reply is owed (cook sent / pan live).
  if (pending || mode === "pan") {
    waitT += dt;
    if (waitT > COOK_TIMEOUT) { sendCookEnd(); exitCookMode(); }
  } else waitT = 0;
}
